import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { renderHelpMarkdown } from '../../components/help/helpMarkdownRenderer';
import { openHelpDoc, parseKnowledgeCitation, slugToHelpDocPath } from '../help/helpOpen';

/** Label → Client portal route (basename /client applied by Router). */
const GO_TO_ROUTES = {
  tickets: '/tickets',
  ticket: '/tickets',
  accounts: '/accounts',
  account: '/accounts',
  invoices: '/invoices',
  invoice: '/invoices',
  payments: '/payments',
  payment: '/payments',
  bonuses: '/bonuses',
  bonus: '/bonuses',
  settings: '/settings',
  notifications: '/notifications',
  dashboard: '/home',
  home: '/home',
};

function normalizeAppPath(href) {
  if (!href) return null;
  let path = String(href).trim();
  if (/^https?:\/\//i.test(path) || path.indexOf('mailto:') === 0) return null;
  if (path.indexOf('kb:') === 0 || path.indexOf('help:') === 0) return null;
  path = path.split('#')[0].split('?')[0];
  if (path.indexOf('/client/') === 0) path = path.slice('/client'.length);
  if (path === '/client') path = '/home';
  if (path.indexOf('/') !== 0) path = `/${path}`;
  return path;
}

function resolveGoToLabel(label) {
  const key = String(label || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  return GO_TO_ROUTES[key] || null;
}

function openCitation(slug) {
  const docPath = slugToHelpDocPath(slug);
  if (docPath) openHelpDoc({ docPath, slug });
}

function linkifyPlain(text, keyPrefix) {
  const s = String(text || '');
  if (!s) return null;

  const citeRe =
    /(\[(?:help-)?(?:admin|client|wifi|reseller)-[a-z0-9-]+(?:@v?\d+)?\]|(?:help-)?(?:admin|client|wifi|reseller)-[a-z0-9-]+@v?\d+)/gi;
  const goRe = /\bGo to ([A-Za-z][A-Za-z0-9 /-]*?)(?=[.!,;:?]?(?:\s|$))/g;

  const matches = [];
  let m;
  citeRe.lastIndex = 0;
  while ((m = citeRe.exec(s)) !== null) {
    matches.push({ start: m.index, end: m.index + m[0].length, kind: 'cite', raw: m[0] });
  }
  goRe.lastIndex = 0;
  while ((m = goRe.exec(s)) !== null) {
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      kind: 'goto',
      raw: m[0],
      label: m[1],
    });
  }
  matches.sort((a, b) => a.start - b.start || b.end - a.end);

  const used = [];
  matches.forEach((hit) => {
    if (used.some((u) => hit.start < u.end && hit.end > u.start)) return;
    used.push(hit);
  });
  used.sort((a, b) => a.start - b.start);

  const nodes = [];
  let last = 0;
  used.forEach((hit, i) => {
    if (hit.start > last) {
      nodes.push(
        <React.Fragment key={`${keyPrefix}-t-${i}-${last}`}>
          {s.slice(last, hit.start)}
        </React.Fragment>
      );
    }
    if (hit.kind === 'cite') {
      const parsed = parseKnowledgeCitation(hit.raw);
      if (parsed) {
        nodes.push(
          <button
            key={`${keyPrefix}-c-${i}`}
            type="button"
            className="portal-ai-cite btn btn-link btn-sm p-0 align-baseline"
            title={`Open ${parsed.slug} help`}
            onClick={(e) => {
              e.preventDefault();
              openCitation(parsed.slug);
            }}
          >
            {hit.raw}
          </button>
        );
      } else {
        nodes.push(
          <React.Fragment key={`${keyPrefix}-craw-${i}`}>{hit.raw}</React.Fragment>
        );
      }
    } else if (hit.kind === 'goto') {
      const to = resolveGoToLabel(hit.label);
      if (to) {
        nodes.push(
          <Link key={`${keyPrefix}-g-${i}`} className="portal-ai-nav" to={to} title={`Go to ${hit.label}`}>
            {hit.raw}
          </Link>
        );
      } else {
        nodes.push(
          <React.Fragment key={`${keyPrefix}-graw-${i}`}>{hit.raw}</React.Fragment>
        );
      }
    }
    last = hit.end;
  });

  if (last < s.length) {
    nodes.push(
      <React.Fragment key={`${keyPrefix}-tail`}>{s.slice(last)}</React.Fragment>
    );
  }

  return nodes.length ? nodes : s;
}

function renderInline(text) {
  const parts = String(text || '')
    .split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`)/g)
    .filter(Boolean);

  return parts.map((part, index) => {
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const label = linkMatch[1];
      const href = linkMatch[2];

      if (/^https?:\/\//i.test(href) || href.indexOf('mailto:') === 0) {
        return (
          <a key={index} href={href} target="_blank" rel="noopener noreferrer">
            {label}
          </a>
        );
      }

      if (href.indexOf('kb:') === 0 || href.indexOf('help:') === 0) {
        const slugOrPath = href.replace(/^(kb|help):/i, '');
        const parsed = parseKnowledgeCitation(slugOrPath);
        const docPath = parsed
          ? slugToHelpDocPath(parsed.slug)
          : slugOrPath.indexOf('.md') >= 0
            ? slugOrPath
            : slugToHelpDocPath(slugOrPath);
        return (
          <button
            key={index}
            type="button"
            className="portal-ai-cite btn btn-link btn-sm p-0 align-baseline"
            onClick={(e) => {
              e.preventDefault();
              if (docPath) openHelpDoc({ docPath, slug: parsed && parsed.slug });
              else if (parsed) openCitation(parsed.slug);
            }}
          >
            {label}
          </button>
        );
      }

      const appPath = normalizeAppPath(href);
      if (appPath) {
        return (
          <Link key={index} className="portal-ai-nav" to={appPath}>
            {label}
          </Link>
        );
      }

      const parsed = parseKnowledgeCitation(label) || parseKnowledgeCitation(href);
      if (parsed) {
        return (
          <button
            key={index}
            type="button"
            className="portal-ai-cite btn btn-link btn-sm p-0 align-baseline"
            onClick={(e) => {
              e.preventDefault();
              openCitation(parsed.slug);
            }}
          >
            {label}
          </button>
        );
      }

      return <React.Fragment key={index}>{label}</React.Fragment>;
    }

    if (part.indexOf('**') === 0 && part.lastIndexOf('**') === part.length - 2) {
      return <strong key={index}>{linkifyPlain(part.slice(2, -2), `b${index}`)}</strong>;
    }
    if (part.indexOf('`') === 0 && part.lastIndexOf('`') === part.length - 1) {
      const inner = part.slice(1, -1);
      const parsed = parseKnowledgeCitation(inner);
      if (parsed) {
        return (
          <button
            key={index}
            type="button"
            className="portal-ai-cite btn btn-link btn-sm p-0 align-baseline"
            onClick={(e) => {
              e.preventDefault();
              openCitation(parsed.slug);
            }}
          >
            {inner}
          </button>
        );
      }
      return <code key={index}>{inner}</code>;
    }

    return <React.Fragment key={index}>{linkifyPlain(part, `p${index}`)}</React.Fragment>;
  });
}

/**
 * Portal AI answers — markdown with Go-to routes + Knowledge citation → Help drawer.
 */
function PortalAiAnswer({ content }) {
  const text = String(content || '');
  if (!text.trim()) return null;
  return (
    <div className="portal-ai-answer">
      {renderHelpMarkdown(text, { renderInline, keyPrefix: 'portal-ai' })}
    </div>
  );
}

PortalAiAnswer.propTypes = {
  content: PropTypes.string,
};

export default PortalAiAnswer;
